import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/lib/errors";
import type {
    CreateContactRequest,
    UpdateContactRequest,
} from "@/api/generated/client/models";

// ===============================
// Contact CRUD
// ===============================

export const CONTACTS_PAGE_SIZE = 50;

export interface ContactsFilters {
    contactType?: string;
    organizationId?: number;
    search?: string;
    page?: number;
    limit?: number;
}

export function useContacts(filters: ContactsFilters = {}) {
    const { contactType, organizationId, search, page = 1, limit = CONTACTS_PAGE_SIZE } = filters;
    const queryClient = useQueryClient();
    const { toast } = useToast();

    const offset = (page - 1) * limit;

    const { data: response, isLoading, isPlaceholderData } = useQuery({
        queryKey: ["contacts", contactType, organizationId, search, page, limit],
        queryFn: async () => {
            return await apiClient.contacts.listContacts({
                contactType,
                organizationId,
                search: search || undefined,
                limit,
                offset,
            });
        },
        placeholderData: keepPreviousData,
    });

    const contacts = response?.contacts || [];
    const total = response?.meta?.total ?? contacts.length;
    const totalPages = Math.ceil(total / limit);

    const createMutation = useMutation({
        mutationFn: async (data: CreateContactRequest) => {
            return await apiClient.contacts.createContact({
                createContactRequest: data,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["contacts"] });
            toast({ title: "Contact created", description: "The contact has been added." });
        },
        onError: async (error: unknown) => {
            const message = await getApiErrorMessage(error);
            toast({ title: "Failed to create contact", description: message, variant: "destructive" });
        },
    });

    const updateMutation = useMutation({
        mutationFn: async ({ id, data }: { id: number; data: UpdateContactRequest }) => {
            return await apiClient.contacts.updateContact({
                id,
                updateContactRequest: data,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["contacts"] });
            toast({ title: "Contact updated" });
        },
        onError: async (error: unknown) => {
            const message = await getApiErrorMessage(error);
            toast({ title: "Failed to update contact", description: message, variant: "destructive" });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: number) => {
            return await apiClient.contacts.deleteContact({ id });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["contacts"] });
            toast({ title: "Contact deleted" });
        },
        onError: async (error: unknown) => {
            const message = await getApiErrorMessage(error);
            toast({ title: "Failed to delete contact", description: message, variant: "destructive" });
        },
    });

    return {
        contacts,
        total,
        totalPages,
        isLoading,
        isPlaceholderData,
        createContact: createMutation.mutateAsync,
        updateContact: updateMutation.mutateAsync,
        deleteContact: deleteMutation.mutate,
        isCreating: createMutation.isPending,
        isUpdating: updateMutation.isPending,
        isDeleting: deleteMutation.isPending,
    };
}

// ===============================
// Contact ↔ Organization
// ===============================

export interface ContactOrganization {
  id: number;
  companyId: number;
  contactId: number;
  organizationId: number;
  role: string | null;
  isPrimary: boolean;
  notes: string | null;
  organizationName?: string;
  orgType?: string;
  createdAt: string;
}

export function useContactOrganizations(contactId: number | null) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data, isLoading } = useQuery({
    queryKey: ["contact-organizations", contactId],
    queryFn: async () => {
      if (!contactId) return { contactOrganizations: [] as ContactOrganization[] };
      const res = await apiClient.contacts.getContactOrganizations({ id: contactId });
      return { contactOrganizations: res.contactOrganizations ?? [] };
    },
    enabled: !!contactId,
  });

  const linkMutation = useMutation({
    mutationFn: async (body: { organizationId: number; role?: string; isPrimary?: boolean; notes?: string }) => {
      if (contactId == null) throw new Error("Cannot link organization: contactId is not set");
      await apiClient.contacts.linkContactOrganization({
        id: contactId,
        linkContactOrganizationRequest: body,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contact-organizations", contactId] });
      toast({ title: "Organization linked" });
    },
    onError: async (error: unknown) => {
      const message = await getApiErrorMessage(error);
      toast({ title: "Failed to link organization", description: message, variant: "destructive" });
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: async (organizationId: number) => {
      if (contactId == null) throw new Error("Cannot unlink organization: contactId is not set");
      await apiClient.contacts.unlinkContactOrganization({
        id: contactId,
        organizationId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contact-organizations", contactId] });
      toast({ title: "Organization unlinked" });
    },
    onError: async (error: unknown) => {
      const message = await getApiErrorMessage(error);
      toast({ title: "Failed to unlink organization", description: message, variant: "destructive" });
    },
  });

  return {
    contactOrganizations: data?.contactOrganizations || [],
    isLoading,
    linkOrganization: linkMutation.mutateAsync,
    unlinkOrganization: unlinkMutation.mutate,
    isLinking: linkMutation.isPending,
  };
}

// ===============================
// Contact ↔ Property
// ===============================

export interface ContactPropertyLink {
  id: number;
  contactId: number;
  propertyId: number;
  addressType: string;
  isPrimary: boolean;
  streetAddress?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  createdAt: string;
}

export function useContactProperties(contactId: number | null) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data, isLoading } = useQuery({
    queryKey: ["contact-properties", contactId],
    queryFn: async () => {
      if (!contactId) return { contactProperties: [] as ContactPropertyLink[] };
      const res = await apiClient.contacts.getContactProperties({ id: contactId });
      return { contactProperties: res.contactProperties ?? [] };
    },
    enabled: !!contactId,
  });

  const linkMutation = useMutation({
    mutationFn: async (body: { propertyId: number; addressType?: string; isPrimary?: boolean }) => {
      if (contactId == null) throw new Error("Cannot link property: contactId is not set");
      await apiClient.contacts.linkContactProperty({
        id: contactId,
        linkContactPropertyRequest: body,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contact-properties", contactId] });
      toast({ title: "Property linked" });
    },
    onError: async (error: unknown) => {
      const message = await getApiErrorMessage(error);
      toast({ title: "Failed to link property", description: message, variant: "destructive" });
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: async (propertyId: number) => {
      if (contactId == null) throw new Error("Cannot unlink property: contactId is not set");
      await apiClient.contacts.unlinkContactProperty({
        id: contactId,
        propertyId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contact-properties", contactId] });
      toast({ title: "Property unlinked" });
    },
    onError: async (error: unknown) => {
      const message = await getApiErrorMessage(error);
      toast({ title: "Failed to unlink property", description: message, variant: "destructive" });
    },
  });

  return {
    contactProperties: data?.contactProperties || [],
    isLoading,
    linkProperty: linkMutation.mutateAsync,
    unlinkProperty: unlinkMutation.mutate,
    isLinking: linkMutation.isPending,
  };
}

// ===============================
// Contact ↔ Claims (reverse lookup)
// ===============================

export interface ContactClaimLink {
  id: number;
  companyId: number;
  claimId: number;
  contactId: number;
  role: string;
  notes: string | null;
  insuredName?: string;
  claimNumber?: string;
  createdAt: string;
}

export function useContactClaims(contactId: number | null, contactType?: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data, isLoading } = useQuery({
    queryKey: ["contact-claims", contactId],
    queryFn: async () => {
      if (!contactId) return { claimContacts: [] as ContactClaimLink[] };
      const res = await apiClient.contacts.getContactClaims({ id: contactId });
      return { claimContacts: res.claimContacts ?? [] };
    },
    enabled: !!contactId,
  });

  const linkMutation = useMutation({
    mutationFn: async (claimId: number) => {
      if (contactId == null) throw new Error("Cannot link claim: contactId is not set");
      await apiClient.claims.linkClaimContact({
        claimId,
        linkClaimContactRequest: { contactId, role: contactType || "Other" },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contact-claims", contactId] });
      toast({ title: "Claim linked" });
    },
    onError: async (error: unknown) => {
      const message = await getApiErrorMessage(error);
      toast({ title: "Failed to link claim", description: message, variant: "destructive" });
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: async (claimId: number) => {
      if (contactId == null) throw new Error("Cannot unlink claim: contactId is not set");
      await apiClient.claims.unlinkClaimContact({ claimId, contactId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contact-claims", contactId] });
      toast({ title: "Claim unlinked" });
    },
    onError: async (error: unknown) => {
      const message = await getApiErrorMessage(error);
      toast({ title: "Failed to unlink claim", description: message, variant: "destructive" });
    },
  });

  return {
    contactClaims: data?.claimContacts || [],
    isLoading,
    linkClaim: linkMutation.mutateAsync,
    unlinkClaim: unlinkMutation.mutate,
  };
}
